import { memo, useEffect, useState } from 'react'
import { ResolvedPersonLiteDto } from '../../api/network/models/getNetworkScopedDtos'
import { PersonDto, PersonLiteDto } from '../../api/person/models/PersonDto'
import { HelixCandidateLiteDto } from '../../api/role/models/CandidateDto'
import { MapExtra } from '../../utils/MapExtra'
import HelixCandidateResultItem from './HelixCandidateResultItem'
import { SharedWorkHistoryLiteDto } from '../../api/role/models/SharedWorkHistoryDto'
import { CompanyDto } from '../../api/company/models/CompanyDto'

interface Props {
    candidates: HelixCandidateLiteDto[]
    personCache: MapExtra<string, PersonDto>
    personLiteCache: MapExtra<string, PersonLiteDto>
    resolvedPersonLites: ResolvedPersonLiteDto[]
    companyCache: MapExtra<string, CompanyDto>
    currentPageNumber: number
    perPage: number
    totalResultCount: number
    pageChanged: (pageNumber: number) => void
    perPageChanged: (perPage: number) => void
    onCandidateSelection?: (candidateId: string) => void
}

const PAGE_SIZES = [10, 25, 50, 100, 200]

const HelixCandidateResultList = memo(function HelixCandidateResultList(
    props: Props,
): React.JSX.Element {
    const {
        candidates,
        personCache,
        personLiteCache,
        resolvedPersonLites,
        companyCache,
        currentPageNumber,
        perPage,
        totalResultCount,
        pageChanged,
        perPageChanged,
        onCandidateSelection,
    } = props

    // useWhyDidYouUpdate('HelixCandidateResultList', props)

    const [transformedResolvedPersons, setTransformedResolvedPersons] =
        useState<TransformedResolvedPerson[]>([])

    // function useWhyDidYouUpdate(name: string, props: Record<string, any>) {
    //     const previousProps = useRef(props)

    //     useEffect(() => {
    //         const allKeys = new Set([
    //             ...Object.keys(previousProps.current),
    //             ...Object.keys(props),
    //         ])
    //         const changesObj: Record<string, { from: any; to: any }> = {}

    //         allKeys.forEach((key) => {
    //             if (previousProps.current[key] !== props[key]) {
    //                 changesObj[key] = {
    //                     from: previousProps.current[key],
    //                     to: props[key],
    //                 }
    //             }
    //         })

    //         if (Object.keys(changesObj).length > 0) {
    //             console.log(`[why-did-you-update] ${name}`, changesObj)
    //         }

    //         previousProps.current = props
    //     }, [props, name])
    // }

    useEffect(() => {
        const newTransformedResolvedPersons =
            transformNetworkScopedRes(resolvedPersonLites)
        setTransformedResolvedPersons(newTransformedResolvedPersons)
    }, [resolvedPersonLites])

    function transformNetworkScopedRes(
        resolvedPersonLites: ResolvedPersonLiteDto[],
    ): TransformedResolvedPerson[] {
        const now = new Date()

        return resolvedPersonLites.map((resolvedPerson) => {
            // Grouping: companyId -> seedPersonId -> shared history
            const companyMap = new Map<
                string,
                Map<string, SharedWorkHistoryLiteDto[]>
            >()

            for (const connection of resolvedPerson.connectionDetails) {
                const seedPersonId = connection.seedPersonId

                for (const swh of connection.sharedWorkHistory) {
                    const { companyId } = swh

                    const companyEntry =
                        companyMap.get(companyId) ??
                        new Map<string, SharedWorkHistoryLiteDto[]>()
                    const existing = companyEntry.get(seedPersonId) ?? []

                    existing.push(swh)
                    companyEntry.set(seedPersonId, existing)
                    companyMap.set(companyId, companyEntry)
                }
            }

            const companies: TransformedCompany[] = Array.from(
                companyMap.entries(),
            ).map(([companyId, seedMap]) => {
                const connections: TransformedConnection[] = Array.from(
                    seedMap.entries(),
                ).map(([seedPersonId, history]) => {
                    const resolvedTitles: string[] = []
                    const seedTitles: string[] = []
                    const overlaps: { start: Date; end: Date }[] = []

                    for (const swh of history) {
                        const {
                            resolvedPersonWorkedAt,
                            seedPersonWorkedAt,
                            startOfOverlap,
                            endOfOverlap,
                        } = swh

                        if (
                            !resolvedPersonWorkedAt.startDate ||
                            !seedPersonWorkedAt.startDate
                        ) {
                            continue
                        }

                        resolvedTitles.push(resolvedPersonWorkedAt.jobTitle)
                        seedTitles.push(seedPersonWorkedAt.jobTitle)

                        const start = new Date(startOfOverlap ?? now)
                        const end = new Date(endOfOverlap ?? now)

                        overlaps.push({ start, end })
                    }

                    const uniqueResolved = [...new Set(resolvedTitles)]
                    const uniqueSeed = [...new Set(seedTitles)]

                    let cumulativeOverlapMs = 0
                    let earliestStart = now
                    let latestEnd = new Date(0)

                    for (const { start, end } of overlaps) {
                        cumulativeOverlapMs += end.getTime() - start.getTime()
                        if (start < earliestStart) earliestStart = start
                        if (end > latestEnd) latestEnd = end
                    }

                    return {
                        seedPersonId,
                        resolvedPersonJobTitles: uniqueResolved,
                        seedPersonJobTitles: uniqueSeed,
                        cumulativeOverlapMs,
                        overallStart: earliestStart,
                        overallEnd: latestEnd,
                    }
                })

                // Sort connections by most recent end date of overlap
                connections.sort((a, b) => {
                    const aEnd = a.overallEnd?.getTime() ?? 0
                    const bEnd = b.overallEnd?.getTime() ?? 0
                    return bEnd - aEnd
                })

                return {
                    companyId,
                    connections,
                }
            })

            // Sort companies by most recent overallEnd (max of all connections)
            companies.sort((a, b) => {
                const aLatest = Math.max(
                    ...a.connections.map((c) => c.overallEnd.getTime()),
                )
                const bLatest = Math.max(
                    ...b.connections.map((c) => c.overallEnd.getTime()),
                )
                return bLatest - aLatest
            })

            return {
                personId: resolvedPerson.personId,
                companies,
            }
        })
    }

    return (
        <div>
            <div className="border-b border-gray-200 pb-4 mb-4">
                <PaginationControls
                    currentPageNumber={currentPageNumber}
                    perPage={perPage}
                    totalResultCount={totalResultCount}
                    pageChanged={pageChanged}
                    perPageChanged={perPageChanged}
                />
            </div>

            {[...candidates]
                .sort((a, b) => a.rank - b.rank)
                .map((candidate) => {
                    const person = personCache.get(candidate.candidatePersonId)
                    if (!person) {
                        throw new Error(
                            `Unable to find person for candidate: ${candidate.candidatePersonId}`,
                        )
                    }

                    const transformedResolvedPerson =
                        transformedResolvedPersons.find(
                            (transformed) =>
                                transformed.personId ===
                                candidate.candidatePersonId,
                        )

                    return (
                        <HelixCandidateResultItem
                            key={candidate.candidatePersonId}
                            candidate={candidate}
                            person={person}
                            companyCache={companyCache}
                            personLiteCache={personLiteCache}
                            transformedResolvedPerson={
                                transformedResolvedPerson
                            }
                            onCandidateSelection={onCandidateSelection}
                        />
                    )
                })}
            <PaginationControls
                currentPageNumber={currentPageNumber}
                perPage={perPage}
                totalResultCount={totalResultCount}
                pageChanged={pageChanged}
                perPageChanged={perPageChanged}
            />
        </div>
    )
})

export default HelixCandidateResultList

function PaginationControls({
    currentPageNumber,
    perPage,
    totalResultCount,
    pageChanged,
    perPageChanged,
}: {
    currentPageNumber: number
    perPage: number
    totalResultCount: number
    pageChanged: (pageNumber: number) => void
    perPageChanged: (perPage: number) => void
}) {
    const totalPages = Math.ceil(totalResultCount / perPage)
    const startIndex = (currentPageNumber - 1) * perPage
    const endIndex = Math.min(startIndex + perPage, totalResultCount)
    const [maxPageButtonsToShow, setMaxPageButtonsToShow] = useState(5)

    useEffect(() => {
        const updatePageButtons = () => {
            setMaxPageButtonsToShow(6)

            // const width = window.innerWidth
            // console.log(`Updating page buttons based on width: ${width}`)
            // if (width > 1500) {
            //     setMaxPageButtonsToShow(11)
            // } else if (width > 992) {
            //     setMaxPageButtonsToShow(9)
            // } else if (width > 768) {
            //     setMaxPageButtonsToShow(7)
            // } else {
            //     setMaxPageButtonsToShow(5)
            // }
        }

        updatePageButtons()
        window.addEventListener('resize', updatePageButtons)
        return () => window.removeEventListener('resize', updatePageButtons)
    }, [])

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        perPageChanged(Number(e.target.value))
    }

    const renderPageNumbers = () => {
        const pages: (number | string)[] = []
        const sideButtons = Math.floor((maxPageButtonsToShow - 3) / 2)

        if (totalPages <= maxPageButtonsToShow) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            const start = Math.max(2, currentPageNumber - sideButtons)
            const end = Math.min(
                totalPages - 1,
                currentPageNumber + sideButtons,
            )

            if (start > 2) pages.push('...')
            for (let i = start; i <= end; i++) pages.push(i)
            if (end < totalPages - 1) pages.push('...')
            pages.push(totalPages)
        }

        return pages.map((page, idx) =>
            typeof page === 'number' ? (
                <button
                    key={idx}
                    className={`px-3 py-1 border rounded ${
                        page === currentPageNumber
                            ? 'bg-ctxt-blue-light text-white'
                            : ''
                    }`}
                    onClick={() => pageChanged(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={idx} className="px-2">
                    {page}
                </span>
            ),
        )
    }

    return (
        <div className="flex items-center justify-between mt-6">
            <div className="flex gap-2 items-center">
                <button
                    onClick={() => pageChanged(currentPageNumber - 1)}
                    disabled={currentPageNumber === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    &lt; Back
                </button>
                {renderPageNumbers()}
                <button
                    onClick={() => pageChanged(currentPageNumber + 1)}
                    disabled={currentPageNumber === totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    Next &gt;
                </button>
            </div>

            <div className="flex items-center gap-4">
                <span>
                    {startIndex + 1}-{endIndex} of {totalResultCount}
                </span>
                <div className="flex items-center gap-1">
                    <label htmlFor="pageSize">Results per page</label>
                    <select
                        id="pageSize"
                        value={perPage}
                        onChange={handlePageSizeChange}
                        className="border rounded p-1"
                    >
                        {PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    )
}

export type TransformedResolvedPerson = {
    personId: string
    companies: TransformedCompany[]
}

export type TransformedCompany = {
    companyId: string
    connections: TransformedConnection[]
}

export type TransformedConnection = {
    seedPersonId: string
    resolvedPersonJobTitles: string[]
    seedPersonJobTitles: string[]
    cumulativeOverlapMs: number
    overallStart: Date
    overallEnd: Date
}
